import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { Select } from '@components/Select';
import FieldWithActions, { rowActions } from '@components/FieldWithActions';
import { useTranslation } from '@hooks';
import { generateTimeOptions } from '@helpers/dates';
import { DEFAULT_HOURS_INTERVAL } from '@constants/companies';

const END_OF_DAY = '24:00';
const ALL_DAY_OPTION = 'allDay';
const ALL_DAY_INTERVAL = { from: '00:00', to: END_OF_DAY };

const List = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '284px',
    '@media (max-width: 600px)': {
        paddingTop: '6px',
    },
});

const TimeSelectWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: 0,
});

const isAllDay = interval => interval.from === ALL_DAY_INTERVAL.from && interval.to === ALL_DAY_INTERVAL.to;

const HoursIntervals = ({ hours, onChange, canAdd = true, canEndAtMidnight = false, canRemoveAll = false }) => {
    const { t } = useTranslation();
    const times = useMemo(() => generateTimeOptions(), []);
    const fromOptions = useMemo(() => [{ value: ALL_DAY_OPTION, label: t('common.24hours') }, ...times], [t, times]);
    const endOfDayOptions = useMemo(() => [...fromOptions, { value: END_OF_DAY, label: END_OF_DAY }], [fromOptions]);
    const allDayOptions = useMemo(
        () => (canEndAtMidnight ? fromOptions : fromOptions.slice(0, -1)),
        [canEndAtMidnight, fromOptions],
    );
    const toOptionsFor = interval => (canEndAtMidnight || interval.to === END_OF_DAY ? endOfDayOptions : fromOptions);

    const nextTimeAfter = value => times[times.findIndex(option => option.value === value) + 1]?.value ?? END_OF_DAY;

    const changedInterval = (interval, type, value) => {
        if (value === ALL_DAY_OPTION) return { ...ALL_DAY_INTERVAL };
        if (isAllDay(interval)) return { from: value, to: nextTimeAfter(value) };
        return { ...interval, [type]: value };
    };

    const changeTime = (index, type, value) =>
        onChange(hours.map((interval, i) => (i === index ? changedInterval(interval, type, value) : interval)));

    const addInterval = () => onChange([...hours, { ...DEFAULT_HOURS_INTERVAL }]);

    const removeInterval = index => onChange(hours.filter((_, i) => i !== index));

    return (
        <List>
            {hours.map((interval, index) => (
                <FieldWithActions
                    key={index}
                    actions={rowActions({
                        index,
                        count: hours.length,
                        onAdd: addInterval,
                        onDelete: () => removeInterval(index),
                        canAdd,
                        canDelete: canRemoveAll || hours.length > 1,
                        t,
                    })}
                >
                    <TimeSelectWrapper>
                        {isAllDay(interval) ? (
                            <Select
                                value={ALL_DAY_OPTION}
                                onChange={e => changeTime(index, 'from', e.target.value)}
                                options={allDayOptions}
                                style={{ width: '100%' }}
                                dropdownStyles={{ maxHeight: '300px' }}
                            />
                        ) : (
                            <>
                                <Select
                                    value={interval.from}
                                    onChange={e => changeTime(index, 'from', e.target.value)}
                                    options={fromOptions}
                                    dropdownStyles={{ maxHeight: '300px' }}
                                    style={{ width: '84px' }}
                                />
                                <span> — </span>
                                <Select
                                    value={interval.to}
                                    onChange={e => changeTime(index, 'to', e.target.value)}
                                    options={toOptionsFor(interval)}
                                    dropdownStyles={{ maxHeight: '300px' }}
                                    style={{ width: '84px' }}
                                />
                            </>
                        )}
                    </TimeSelectWrapper>
                </FieldWithActions>
            ))}
        </List>
    );
};

export default HoursIntervals;
